/**
 * Tier B -> A upgrade: additive block injection.
 * Per D-13: existing blocks preserved, CRUD/auth/payment blocks injected additively.
 * Per D-14: no re-generation -- existing customizations survive.
 */

export interface UpgradeBlockTemplate {
  name: string
  type: string
  description: string
  flows?: Array<{ id: string; name: string; steps: unknown[] }>
}

export interface UpgradeResult {
  success: boolean
  injectedBlocks: string[]
  injectedFlows: string[]
  error?: string
}

// Block types that Tier A has but Tier B lacks
const UPGRADE_BLOCK_TEMPLATES: UpgradeBlockTemplate[] = [
  {
    name: '__upgrade_auth_login',
    type: 'auth',
    description: 'Login form with email/password',
    flows: [{
      id: '__upgrade_flow_login',
      name: '__upgrade_Login Flow',
      steps: [
        { type: 'validate_input', fields: ['email', 'password'] },
        { type: 'supabase_auth', method: 'signInWithPassword' },
      ],
    }],
  },
  {
    name: '__upgrade_auth_signup',
    type: 'signup',
    description: 'Registration form with email/password',
    flows: [{
      id: '__upgrade_flow_signup',
      name: '__upgrade_Signup Flow',
      steps: [
        { type: 'validate_input', fields: ['email', 'password', 'name'] },
        { type: 'supabase_auth', method: 'signUp' },
      ],
    }],
  },
  {
    name: '__upgrade_crud_admin',
    type: 'crud',
    description: 'CRUD management panel',
    flows: [
      { id: '__upgrade_flow_crud_list', name: '__upgrade_CRUD List Flow', steps: [{ type: 'supabase_select', table: '__data' }] },
      { id: '__upgrade_flow_crud_create', name: '__upgrade_CRUD Create Flow', steps: [{ type: 'validate_input' }, { type: 'supabase_insert', table: '__data' }] },
      { id: '__upgrade_flow_crud_update', name: '__upgrade_CRUD Update Flow', steps: [{ type: 'validate_input' }, { type: 'supabase_update', table: '__data' }] },
      { id: '__upgrade_flow_crud_delete', name: '__upgrade_CRUD Delete Flow', steps: [{ type: 'supabase_delete', table: '__data' }] },
    ],
  },
  {
    name: '__upgrade_dashboard',
    type: 'dashboard',
    description: 'Admin dashboard with data overview',
  },
]

export interface AppBlocks {
  blocks: Array<{ name: string; type: string; [key: string]: unknown }>
  flows?: Array<{ id: string; name: string; [key: string]: unknown }>
  tier?: string
}

export function upgradeToTierA(app: AppBlocks): UpgradeResult {
  const existingTypes = new Set(app.blocks.map((b) => b.type?.toLowerCase()))
  const existingFlowNames = new Set((app.flows ?? []).map((f) => f.name))

  const toInject = UPGRADE_BLOCK_TEMPLATES.filter((t) => !existingTypes.has(t.type))

  if (toInject.length === 0) {
    // Still update tier metadata even if no blocks needed
    app.tier = 'A'
    return { success: true, injectedBlocks: [], injectedFlows: [] }
  }

  const injectedBlocks: string[] = []
  const injectedFlows: string[] = []

  for (const template of toInject) {
    // Add block (additive -- does not modify existing)
    app.blocks.push({
      name: template.name,
      type: template.type,
      description: template.description,
    })
    injectedBlocks.push(template.name)

    // Add flows with __upgrade_ prefix to avoid collisions
    if (template.flows) {
      for (const flow of template.flows) {
        if (!existingFlowNames.has(flow.name)) {
          if (!app.flows) app.flows = []
          app.flows.push({ ...flow })
          injectedFlows.push(flow.name)
        }
      }
    }
  }

  // Update tier metadata
  app.tier = 'A'

  return { success: true, injectedBlocks, injectedFlows }
}
